/* eslint-disable max-lines */
import { signInWithRedirect, fetchAuthSession } from 'aws-amplify/auth'
import Environment from 'helpers/environment'
import ApiError from 'services/ApiError'

import { AdminNotificationsPolicy } from 'types/AdminNotificationsPolicy'
import { ArchivedDevice } from 'types/ArchivedDevice'
import { Customer } from 'types/Customer'
import { Device } from 'types/Device'
import { ModifiedDeviceCreatePayload } from 'types/ModifiedDeviceCreatePayload'
import { ModifiedDeviceUpdateServiceContractPayload } from 'types/ModifiedDeviceUpdateServiceContractPayload'
import { ModifiedSystemCreatePayload } from 'types/ModifiedSystemCreatePayload'
import { MutedNotificationsUpdatePayload } from 'types/MutedNotificationsUpdatePayload'
import { ResourceRequest } from 'types/Resource'
import { RoleFeature } from 'types/RoleFeature'
import { SystemComponent } from 'types/SystemComponent'
import { SystemConfiguration } from 'types/SystemConfigReport'
import { SystemReportData } from 'types/SystemReportData'
import { BillingData } from 'types/BillingData'
import { CallToAction } from 'types/DeviceMetric'
import { PremiumFeature } from 'types/PremiumFeature'

let serverErrorFn: (errorMessage: string) => void
export const setServerErrorFn = (fn: (errorMessage: string) => void) => {
  serverErrorFn = fn
}

type Props = {
  path: string
  method?: 'GET' | 'PUT' | 'PATCH' | 'POST' | 'DELETE'
  payload?: any // eslint-disable-line
  showBannerError?: boolean
  fetchOptions?: { signal: AbortSignal }
  isFile?: boolean
}

const processResponse = (response: Response, showBannerError: boolean, isFile: boolean) => {
  if (response.status >= 500) {
    if (showBannerError) { serverErrorFn('SERVER ERROR') }
    throw new ApiError('Server Error', response)
  } else if (response.status > 401 && response.status < 500) {
    throw new ApiError('Server Error', response)
  } else if (response.status === 401) {
    throw new ApiError('Unauthorized', response)
  } else if (response.status === 400) {
    return Promise.reject(response)
  } else if (response.status === 204) {
    return null
  } else if (response.status === 304) {
    return null
  } else if (isFile && response.status === 200) {
    return response.blob()
  } else {
    return response.json()
  }
}

const fetchFromApi = async ({ path, method = 'GET', payload = null, showBannerError = true, fetchOptions, isFile = false }: Props) => {
  let session

  if (Environment.isAWSCognito()) {
    session = await fetchAuthSession()

    if (!session?.tokens?.idToken) {
      await signInWithRedirect({ provider: { custom: 'AzureB2C' } })
      return
    }
  }

  return await fetch(`${Environment.backendApiURL()}/api${path}`, Object.assign({
    method,
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(Environment.isAWSCognito() && { 'COGNITO-Authorization': `${session.tokens.idToken}` })
    },
    body: payload ? JSON.stringify(payload) : null
  }, fetchOptions) as RequestInit).then((response) => {
    return processResponse(response, showBannerError, isFile)
  })
}

const buildParams = (unformattedParams = {}) => {
  const formattedParams = new URLSearchParams()

  Object.keys(unformattedParams).forEach((key) => {
    if (unformattedParams[key] === undefined) { return }

    return formattedParams.append(key, unformattedParams[key])
  })

  return `${formattedParams.toString()}`
}

// Please keep this list alphabetized
const Api = {
  AlertDescriptions: {
    create: (payload: { code: string }) => fetchFromApi({ path: '/alarm_descriptions', method: 'POST', payload }),
    delete: (code: string) => fetchFromApi({ path: `/alarm_descriptions/${code}`, method: 'DELETE' }),
    list: () => fetchFromApi({ path: '/alarm_descriptions' }),
    update: (payload: { code: string }) => fetchFromApi({ path: `/alarm_descriptions/${payload.code}`, method: 'PATCH', payload })
  },
  AlertGuide: {
    getCSV: () => fetchFromApi({ path: '/alert_guide/report', isFile: true }),
    getExcel: () => fetchFromApi({ path: '/alert_guide/report/xlsx', isFile: true })
  },
  AlertNotifications: {
    get: (code: string) => fetchFromApi({ path: `/alert_notifications/${code}`, method: 'GET' }),
    list: ({ page = 1, sortAttribute = '', sortDirection = '', query = '' } = {}) => { // TODO: use buildParams method for everything after '?'
      let url = `/alert_notifications?page_size=20&page_number=${page}&sort_option=${sortAttribute?.toUpperCase()}_${sortDirection?.toUpperCase()}`
      if (query) {
        url += `&search_term=${query.trim()}`
      }
      return fetchFromApi({ path: url })
    },
    update: (payload: Partial<AdminNotificationsPolicy>, code: string) => fetchFromApi({ path: `/alarm_notifications/${code}`, method: 'PATCH', payload })
  },
  AlertsHistory: {
    get: (dealerId: number, deviceId: string, fromTime: string, toTime: string, deviceTimeZone: string) => { // TODO: use buildParams method for everything after '?'
      return fetchFromApi({ path: `/dealers/${dealerId}/devices/${deviceId}/alarm_history?begin_time=${fromTime}&end_time=${toTime}&time_zone=${deviceTimeZone}` })
    }
  },
  ArchivedDevices: {
    create: (dealerId: number, payload: Omit<ArchivedDevice, 'id' | 'createdAt'>) => fetchFromApi({ path: `/dealers/${dealerId}/archived_devices`, method: 'POST', payload }),
    delete: (dealerId: number, archivedDeviceId: number) => fetchFromApi({ path: `/dealers/${dealerId}/archived_devices/${archivedDeviceId}`, method: 'DELETE' }),
    list: (dealerId: number) => fetchFromApi({ path: `/dealers/${dealerId}/devices/archived` }),
    update: (dealerId: number, archivedDeviceId: number, payload: ArchivedDevice) => fetchFromApi({ path: `/dealers/${dealerId}/archived_devices/${archivedDeviceId}`, method: 'PATCH', payload })
  },
  AwairEvents: {
    list: (deviceId: string, awairDeviceId: number, date: string) => fetchFromApi({ path: `/devices/${deviceId}/awair_device/${awairDeviceId}/awair_events/${date}` })
  },
  ConsolidatedEvents: {
    get: (deviceId: string, date: string) => fetchFromApi({ path: `/devices/${deviceId}/consolidated_events/${date}` }),
    list: () => fetchFromApi({ path: '/consolidated_events' })
  },
  Customer: {
    create: (dealerId: string, payload: Customer) => fetchFromApi({ path: `/dealers/${dealerId}/customers`, method: 'POST', payload }),
    delete: (dealerId: string, customerId: string) => fetchFromApi({ path: `/dealers/${dealerId}/customers/${customerId}`, method: 'DELETE' }),
    get: (dealerId: string, customerId: string) => fetchFromApi({ path: `/dealers/${dealerId}/customers/${customerId}` }),
    update: (dealerId: number, customerId: string, payload: Customer) => fetchFromApi({ path: `/dealers/${dealerId}/customers/${customerId}`, method: 'PATCH', payload })
  },
  CustomerAdoption: {
    get: (dealerId: string) => fetchFromApi({ path: `/dealers/${dealerId}/get_customers_for_customer_adoption` })
  },
  Customers: {
    bulkUpdateFlag: (dealerId: string, payload: { flag_type: string, sync_home: boolean }) => fetchFromApi({ path: `/dealers/${dealerId}/customers/bulk_update_flag`, method: 'POST', payload }),
    criticalAndMajorAlerts: (dealerId: string) => fetchFromApi({ path: `/dealers/${dealerId}/customers_with_critical_and_major_alerts`, method: 'GET' }),
    getPiiInfo: (dealerId: string) => fetchFromApi({ path: `/dealers/${dealerId}/pii_info/customers`, method: 'GET' }),
    list: (dealerId: string, { page = 1, sortAttribute = '', sortDirection = '', query = '', filters = '', size = '10' } = {}) => { // TODO: use buildParams method for everything after '?'
      let url = `/dealers/${dealerId}/paged/customers?page_size=${size}&page_number=${page}&sort_option=${sortAttribute?.toUpperCase()}_${sortDirection?.toUpperCase()}`
      if (query) {
        url += `&search_term=${query.trim()}`
      }
      if (filters) {
        url += `&filter_string=${filters.trim()}`
      }
      return fetchFromApi({ path: url })
    },
    pinned: (dealerId: string) => fetchFromApi({ path: `/dealers/${dealerId}/pinned_customers` }),
    search: (dealerId: string, searchTerm: string | null) => fetchFromApi({ path: `/dealers/${dealerId}/customer_search/${searchTerm}`, method: 'GET' })
  },
  ConsumerRth: {
    getDaily: (deviceId: string) => fetchFromApi({ path: `/consumer_rth/devices/${deviceId}/daily`, showBannerError: false }),
    getMonthly: (deviceId: string) => fetchFromApi({ path: `/consumer_rth/devices/${deviceId}/monthly`, showBannerError: false })
  },
  DealerImage: {
    updateImage: (dealerId: number, image: string) => fetchFromApi({ path: `/admin/dealers/${dealerId}/image`, method: 'PATCH', payload: { image } }),
    deleteImage: (dealerId: number) => fetchFromApi({ path: `/admin/dealers/${dealerId}/image`, method: 'DELETE' })
  },
  Dealers: {
    contactInfo: (dealerId: number) => fetchFromApi({ path: `/dealers/${dealerId}/contact_info` }),
    get: (dealerId: number) => fetchFromApi({ path: `/dealers/${dealerId}` }),
    list: ({ page = 1, pageSize = 10, query = '' } = {}) => { // TODO: use buildParams method for everything after '?'
      let url = `/admin/dealers?page_size=${pageSize}&page_number=${page}`
      if (query) {
        url += `&search_term=${query.trim()}`
      }
      return fetchFromApi({ path: url })
    },
    migrate: (fromDealerId: string, toDealerID: string) => fetchFromApi({ path: `/admin/dealers/migrate/${fromDealerId}/${toDealerID}`, method: 'PUT' }),
    piiFlags: (dealerId: string) => fetchFromApi({ path: `/dealers/${dealerId}/pii_flags` }),
    getDeviceStatus: (dealerId: string) => fetchFromApi({ path: `/dealers/${dealerId}/device_status` })
  },
  DefaultFirmwareLoads: {
    get: () => fetchFromApi({ path: '/default_firmware_loads', showBannerError: false })
  },
  Device: {
    create: (payload: ModifiedDeviceCreatePayload | ModifiedSystemCreatePayload) => fetchFromApi({ path: '/device', method: 'POST', payload }),
    delete: (deviceId: string) => fetchFromApi({ path: `/device/${deviceId}`, method: 'DELETE' }),
    get: (dealerId: string, deviceId: string) => fetchFromApi({ path: `/dealers/${dealerId}/devices/${deviceId}` }),
    migrate: (deviceId: string, fromDealerId: string, toDealerId: number | undefined) => fetchFromApi({ path: `/devices/${deviceId}/migrate/${fromDealerId}/${toDealerId}`, method: 'PUT' }),
    update: (dealerId: number, deviceId: string, payload: Pick<Device, 'note'> | Device) => fetchFromApi({ path: `/dealers/${dealerId}/devices/${deviceId}`, method: 'PATCH', payload }),
    updateServiceContract: (dealerId: number, deviceId: string, payload: ModifiedDeviceUpdateServiceContractPayload) => fetchFromApi({ path: `/dealers/${dealerId}/devices/${deviceId}/service_contract`, method: 'PATCH', payload })
  },
  DeviceAggregator: {
    getCustomer: (deviceId: string) => fetchFromApi({ path: `/admin/devices/${deviceId}/customer_details` }),
    getCurrentDeviceState: (deviceId: string) => fetchFromApi({ path: `/admin/devices/${deviceId}/current_device_state` }),
    getDeviceDetails: (deviceId: string) => fetchFromApi({ path: `/admin/devices/${deviceId}/device_details` }),
    getDeviceState: (deviceId: string, date: string) => fetchFromApi({ path: `/admin/devices/${deviceId}/device_state?date=${date}` }), // TODO: use buildParams method for everything after '?'
    getFaceplateMessages: (deviceId: string, startTime: string, endTime: string) => fetchFromApi({ path: `/admin/devices/${deviceId}/faceplate_messages?start_time=${startTime}&end_time=${endTime}` }), // TODO: use buildParams method for everything after '?'
    getFaceplateMessagesCSV: (deviceId: string, params: { startTime: string, endTime: string }) => fetchFromApi({ path: `/admin/devices/${deviceId}/faceplate_messages/csv?${buildParams(params)}`, isFile: true }),
    triggerPastFaceplateMessagesCSV: (deviceId: string) => fetchFromApi({ path: `/admin/devices/${deviceId}/faceplate_messages/past/csv/trigger` }),
    getPastFaceplateMessagesCSVStatus: (deviceId: string, jobId: string) => fetchFromApi({ path: `/admin/devices/${deviceId}/faceplate_messages/past/csv/job_status?job_id=${jobId}` })
  },
  Devices: {
    search: (q: string[]) => fetchFromApi({ path: `/devices?q=${q}` })
  },
  DiagnosticsCustomers: {
    update: (dealerId: string, customerId: string, payload: { pinned: boolean }) => fetchFromApi({ path: `/dealers/${dealerId}/diagnostics/customers/${customerId}`, method: 'PATCH', payload })
  },
  Favorite: {
    create: (userId: number, deviceId: string) => fetchFromApi({ path: `/users/${userId}/favorites/${deviceId}`, method: 'POST' }),
    delete: (userId: number, deviceId: string) => fetchFromApi({ path: `/users/${userId}/favorites/${deviceId}`, method: 'DELETE' }),
    list: (userId: number) => fetchFromApi({ path: `/users/${userId}/favorites` })
  },
  FeatureCodes: {
    addDealerFeature: (dealerId: string, featureCode: string | null) => fetchFromApi({ path: `/dealers/${dealerId}/feature_code/${featureCode}`, method: 'POST' }),
    dealerFeatures: (dealerId: string) => fetchFromApi({ path: `/dealers/${dealerId}/dealer_features` }),
    enabledFeatures: (dealerId: string) => fetchFromApi({ path: `/dealers/${dealerId}/enabled_features` }),
    removeDealerFeature: (dealerId: string, featureCode: string) => fetchFromApi({ path: `/dealers/${dealerId}/feature_code/${featureCode}`, method: 'DELETE' }),
    roleFeatures: () => fetchFromApi({ path: '/admin/role_features' }),
    updateRoleFeatures: (roleFeatures: { roleFeatures: RoleFeature[] }) => fetchFromApi({ path: '/admin/role_features', method: 'PATCH', payload: roleFeatures })
  },
  FeatureTours: {
    create: (featureTour: { id: string }) => fetchFromApi({ path: '/admin/feature_tours', method: 'POST', payload: featureTour }),
    list: (brands: string, getPublishedOnly: boolean) => fetchFromApi({ path: `/admin/feature_tours?brands=${brands}&published_only=${getPublishedOnly}` }),
    update: (featureTour: { id: string }) => fetchFromApi({ path: `/admin/feature_tours/${featureTour.id}`, method: 'PATCH', payload: featureTour }),
    archive: (featureTourId: number) => fetchFromApi({ path: `/admin/feature_tours/${featureTourId}/archive`, method: 'PUT' }),
    restore: (featureTourId: number) => fetchFromApi({ path: `/admin/feature_tours/${featureTourId}/restore`, method: 'PUT' }),
    delete: (featureTourId: { id: string }) => fetchFromApi({ path: `/admin/feature_tours/${featureTourId}`, method: 'DELETE' })
  },
  FeatureTourSiteMessages: {
    list: (featureTourId: number) => fetchFromApi({ path: `/admin/feature_tour_site_messages/${featureTourId}` })
  },
  FeatureTourUserRead: {
    list: (brand: string) => fetchFromApi({ path: `/feature_tour_user_read?brand=${brand}` }),
    create: (payload: { featureTourId: number, brand: string }) => fetchFromApi({ path: '/feature_tour_user_read', method: 'POST', payload })
  },
  HomeownerAlerts: {
    get: (deviceId: string) => fetchFromApi({ path: `/homeowner_alerts/${deviceId}` })
  },
  MutedNotifications: {
    update: (payload: MutedNotificationsUpdatePayload) => fetchFromApi({ path: '/muted_notifications', method: 'PATCH', payload })
  },
  Notes: {
    create: (dealerId: number, deviceId: string, content: string) => fetchFromApi({ path: `/dealers/${dealerId}/devices/${deviceId}/note`, method: 'POST', payload: { content } })
  },
  NotificationRecipients: {
    get: (dealerId: number, notificationRecipientId: number) => fetchFromApi({ path: `/dealers/${dealerId}/notification_recipients/${notificationRecipientId}` }),
    create: (payload: { dealerId: string }) => fetchFromApi({ path: `/dealers/${payload.dealerId}/notification_recipients`, method: 'POST', payload }),
    delete: (dealerId: string, notificationRecipientId: string) => fetchFromApi({ path: `/dealers/${dealerId}/notification_recipients/${notificationRecipientId}`, method: 'DELETE' }),
    list: (dealerId: string) => fetchFromApi({ path: `/dealers/${dealerId}/notification_recipients` }),
    update: (payload: { dealerId: string, id: string }) => fetchFromApi({ path: `/dealers/${payload.dealerId}/notification_recipients/${payload.id}`, method: 'PATCH', payload }),
    verify_email: (payload) => fetchFromApi({ path: `/notification_recipients/verify_email?token=${payload.token}`, method: 'PATCH' })
  },
  Paywall: {
    getPlans: () => fetchFromApi({ path: '/paywall/plans' }),
    getActiveAcct: (dealerId: number) => fetchFromApi({ path: `/paywall/active_account/${dealerId}` }),
    getPremiumAcct: (dealerId: number) => fetchFromApi({ path: `/paywall/premium_account/${dealerId}` }),
    getAcctPlanChange: (dealerId: number) => fetchFromApi({ path: `/paywall/account/plan_change/${dealerId}` }),
    getStatements: (dealerId: number) => fetchFromApi({ path: `/paywall/statements/${dealerId}` }),
    getInvoice: (accountNo: number, invoiceNo: number) => fetchFromApi({ path: `/paywall/invoice/${accountNo}/${invoiceNo}` }),
    premiumFeatures: (dealerId: string) => fetchFromApi({ path: `/dealers/${dealerId}/premium_features` }),
    triggerCybersource: (dealerId: number, planNo: number, content: BillingData) => fetchFromApi({ path: `/paywall/cybersource/${dealerId}/${planNo}`, method: 'POST', payload: { content } }),
    setupTestAccount: (dealerId: number, planNo: number, content: BillingData) => fetchFromApi({ path: `/paywall/test_account/${dealerId}/${planNo}`, method: 'POST', payload: { content } }),
    create: (content: { plan: number; firstName: string; lastName: string }) => fetchFromApi({ path: '/paywall/create_account', method: 'POST', payload: { content } }),
    updatePlan: (dealerId: number, planNo: number) => fetchFromApi({ path: `/paywall/account_plan/${dealerId}`, method: 'POST', payload: { plan_no: planNo } }),
    cancelPlan: (dealerId: number) => fetchFromApi({ path: `/paywall/cancel_plan/${dealerId}`, method: 'POST' })
  },
  PremiumFeatures: {
    list: () => fetchFromApi({ path: '/admin/premium_features' }),
    update: (payload: PremiumFeature) => fetchFromApi({ path: '/admin/premium_features', method: 'PUT', payload })
  },
  ProductLiterature: {
    download: (pdfUrl: string) => fetchFromApi({ path: `/product_literature/download?pdf_url=${pdfUrl}`, isFile: true }),
    get: (modelNumber: string) => fetchFromApi({ path: `/product_literature?model_number=${modelNumber}` })
  },
  RemoteConfig: {
    compatible: (deviceId: string) => fetchFromApi({ path: `/remote_config/${deviceId}/compatible` }),
    list: (states: string[]) => fetchFromApi({ path: `/remote_config/status?states=${states.join(',')}`, method: 'GET' }), // TODO: use buildParams method for everything after '?'
    requestAccess: (deviceId: string, payload: { duration: number, message: string }) => fetchFromApi({ path: `/remote_config/${deviceId}/request_access?origination=DWA`, method: 'POST', payload }),
    status: (deviceId: string) => fetchFromApi({ path: `/remote_config/${deviceId}/status` }),
    stop: (deviceId: string) => fetchFromApi({ path: `/remote_config/${deviceId}/stop`, method: 'POST' }),
    iframeUrl: (deviceId: string, authToken: string) => Environment.backendApiURL() + `/api/remote_config/${deviceId}/iframe_html?COGNITO_AUTHORIZATION=${authToken}`
  },
  Resources: {
    list: (brand, publishedOnly) => fetchFromApi({ path: `/resources?brand=${brand}&published_only=${publishedOnly}` }),
    update: (resource: ResourceRequest) => fetchFromApi({ path: '/resources', method: 'POST', payload: resource }),
    delete: (id: string, type: 'video' | 'article' | 'faq' | 'thermostat') => fetchFromApi({ path: `/resources/${id}?type=${type}`, method: 'DELETE' })
  },
  RuntimeHistory: { // TODO: use buildParams method for everything after '?'
    get: (dealerId: string, deviceId: string, startTime: string, endTime: string, timeZone: string, fetchOptions: { signal: AbortSignal }) => fetchFromApi({ path: `/dealers/${dealerId}/devices/${deviceId}/runtime_history?start_time=${startTime}&end_time=${endTime}&time_zone=${timeZone}`, method: 'GET', payload: null, showBannerError: true, fetchOptions }),
    getCSV: (dealerId: string, deviceId: string, startTime: string, endTime: string, deviceType: string, timeZone: string) => fetchFromApi({ path: `/dealers/${dealerId}/devices/${deviceId}/csv_runtime_history?start_time=${startTime}&end_time=${endTime}&device_type=${deviceType}&time_zone=${timeZone}`, isFile: true })
  },
  SearchHistory: {
    modify: (userId: number, type: string, query: string) => fetchFromApi({ path: `/search_history/users/${userId}/${type}`, method: 'POST', payload: { query } }),
    list: (userId: number, type: string) => fetchFromApi({ path: `/search_history/users/${userId}/${type}` })
  },
  Session: {
    get: () => fetchFromApi({ path: '/sessions' }),
    getJwt: () => fetchFromApi({ path: '/sessions/jwt' }),
    update: (payload: { impersonateDealerId?: boolean, termsUpToDate?: boolean }) => fetchFromApi({ path: '/sessions', method: 'PATCH', payload }),
    logout: Environment.backendApiURL() + '/api/sessions/logout'
  },
  SiteMessageBanners: { // TODO: use buildParams method for everything after '?'
    list: (brands: string, currentMessageLastUpdatedAt: string) => fetchFromApi({ path: `/admin/site_messages?brands=${brands}&filter_by_site_banner_enabled=true&current_message_last_updated_at=${currentMessageLastUpdatedAt || ''}` })
  },
  SiteMessages: {
    create: (siteMessage: { id: string }) => fetchFromApi({ path: '/admin/site_messages', method: 'POST', payload: siteMessage }),
    delete: (siteMessageId: { id: string }) => fetchFromApi({ path: `/admin/site_messages/${siteMessageId}`, method: 'DELETE' }),
    list: (brands: Array<string>[], getPublishedOnly: boolean) => fetchFromApi({ path: `/admin/site_messages?brands=${brands}&published_only=${getPublishedOnly}` }), // TODO: use buildParams method for everything after '?'
    update: (siteMessage: { id: string }) => fetchFromApi({ path: `/admin/site_messages/${siteMessage.id}`, method: 'PATCH', payload: siteMessage }),
    archive: (siteMessageId: number) => fetchFromApi({ path: `/admin/site_messages/${siteMessageId}/archive`, method: 'PUT' }),
    restore: (siteMessageId: number) => fetchFromApi({ path: `/admin/site_messages/${siteMessageId}/restore`, method: 'PUT' }),
    availableSiteMessages: (featureTourId: string, brands: string, roles: string) => fetchFromApi({ path: `/admin/site_messages/available?brands=${brands}&roles=${roles}&feature_tour_id=${featureTourId}` }),
    toursForSiteMessage: (siteMessageId: number) => fetchFromApi({ path: `/admin/feature_tour_site_messages/site_message/${siteMessageId}` })
  },
  SiteMessagesUserRead: {
    create: (payload: { siteMessageIds: number[] }) => fetchFromApi({ path: '/site_messages_user_read', method: 'POST', payload }),
    getUnread: () => fetchFromApi({ path: '/site_messages/unread' }),
    getArchived: () => fetchFromApi({ path: '/site_messages_user_read/archived' }),
    getRemoved: () => fetchFromApi({ path: '/site_messages_user_read/removed' }),
    archive: (siteMessageId: number) => fetchFromApi({ path: `/site_messages_user_read/${siteMessageId}/archive`, method: 'PUT' }),
    remove: (siteMessageId: number) => fetchFromApi({ path: `/site_messages_user_read/${siteMessageId}`, method: 'DELETE' }),
    restore: (siteMessageId: number) => fetchFromApi({ path: `/site_messages_user_read/${siteMessageId}/restore`, method: 'PUT' })
  },
  SystemComponents: {
    create: (deviceId: string, payload: SystemComponent) => fetchFromApi({ path: `/devices/${deviceId}/system_components`, method: 'POST', payload }),
    delete: (deviceId: string, id: number | null) => fetchFromApi({ path: `/devices/${deviceId}/system_components/${id}`, method: 'DELETE' }),
    get: (deviceId: string) => fetchFromApi({ path: `/devices/${deviceId}/system_components` }),
    update: (deviceId: string, id: number, payload: SystemComponent) => fetchFromApi({ path: `/devices/${deviceId}/system_components/${id}`, method: 'PATCH', payload }),
    report: (deviceId: string, params: { filename: string }) => fetchFromApi({ path: `/devices/${deviceId}/system_components/warranty/report?${buildParams(params)}`, isFile: true }),
    getEquipmentImage: (modelNumber: string, showBannerError = false) => {
      return fetchFromApi({ path: `/system_components/images/${modelNumber}`, method: 'GET', payload: null, showBannerError })
    }
  },
  SystemConfig: {
    get: (deviceId: string) => fetchFromApi({ path: `/systems/${deviceId}/configuration` }),
    update: (deviceId: string, configuration: SystemConfiguration) => fetchFromApi({ path: `/systems/${deviceId}/configuration`, method: 'PATCH', payload: configuration })
  },
  SystemReports: {
    list: (deviceId: string) => fetchFromApi({ path: `/system_reports/${deviceId}` }),
    getReport: (params: SystemReportData) => fetchFromApi({ path: `/system_reports?${buildParams(params)}`, isFile: true }),
    uploadReportImage: (image: string, customerId: string) => fetchFromApi({ path: '/system_reports/upload_report_image', method: 'POST', payload: { image, customerId } }),
    sendEmail: (payload: SystemReportData) => fetchFromApi({ path: '/system_reports/send_email', method: 'POST', payload })
  },
  DeviceMetrics: {
    create: (payload: { id: string }) => fetchFromApi({ path: '/admin/device_metrics', method: 'POST', payload }),
    list: () => fetchFromApi({ path: '/admin/device_metrics' }),
    update: (payload: { id: string, name: string, description: string, callToAction: CallToAction[], draft: boolean, unit: string, sort: string }) => fetchFromApi({ path: `/admin/device_metrics/${payload.id}`, method: 'PATCH', payload }),
    delete: (metricId: string) => fetchFromApi({ path: `/admin/device_metrics/${metricId}`, method: 'DELETE' }),
    getDeviceMetrics: (deviceId: string, date: string) => fetchFromApi({ path: `/devices/${deviceId}/device_metrics/${date}` }),
    getDeviceMaintenanceTrends: (deviceId: string) => fetchFromApi({ path: `/devices/${deviceId}/maintenance_trends` }),
    getGroupsList: () => fetchFromApi({ path: '/admin/device_metrics/groups', method: 'GET' }),
    createGroupMetric: (payload) => fetchFromApi({ path: '/admin/device_metrics/groups', method: 'POST', payload }),
    updateGroupMetric: (payload) => fetchFromApi({ path: `/admin/device_metrics/groups/${payload.groupId}`, method: 'PATCH', payload }),
    deleteGroupMetric: (groupId) => fetchFromApi({ path: `/admin/device_metrics/groups/${groupId}`, method: 'DELETE' }),
    getTrends: () => fetchFromApi({ path: '/admin/device_metrics/trends', method: 'GET' }),
    getTrendMetricValues: (metricId: string) => fetchFromApi({ path: `/admin/device_metrics/trends/preview?metric_id=${metricId}`, method: 'GET' }),
    createTrend: (payload) => fetchFromApi({ path: '/admin/device_metrics/trends', method: 'POST', payload }),
    updateTrend: (payload) => fetchFromApi({ path: `/admin/device_metrics/trends/${payload.id}`, method: 'PATCH', payload }),
    deleteTrend: (id) => fetchFromApi({ path: `/admin/device_metrics/trends/${id}`, method: 'DELETE' }),
    sortTrend: (payload) => fetchFromApi({ path: '/admin/device_metrics/trends/sort', method: 'PUT', payload })
  },
  UnassignedDevices: {
    list: (dealerId: number) => fetchFromApi({ path: `/dealers/${dealerId}/devices/unassigned` })
  },
  VerificationReports: {
    list: (deviceId: string) => fetchFromApi({ path: `/verification_reports/${deviceId}` })
  },
  WarrantyInfo: {
    get: (serialNumber: string, retrieveCached = false, showBannerError = false) => {
      return fetchFromApi({ path: `/warranty_info/${serialNumber}?retrieve_cached=${retrieveCached}`, method: 'GET', payload: null, showBannerError })
    }
  },
  WistiaVideo: {
    get: (videoId: string) => fetchFromApi({ path: `/wistia_videos/${videoId}` }),
    getFTHVideo: (q: string) => fetchFromApi({ path: `/wistia_videos/fth/videos?search_text=${q}` })
  },
  UnsubscribeEmails: {
    create: (email: string, type: string) => fetchFromApi({ path: `/unsubscribed_emails/unsubscribe?email=${email}&type=${type}`, method: 'POST' }),
    get: (email: string, type: string) => fetchFromApi({ path: `/unsubscribed_emails/unsubscribed?email=${email}&type=${type}` })
  }
}

export default Api
